import type { AgentStatus } from '@entities/agent-status';
import {
  ConfigureOmniChannelOn,
  IntegrationSettingKeys,
  IntegrationSettingsValueType,
  type IntegrationSettings,
} from '@entities/integration';
import type { StatusSyncCrmToAircallInput } from '@infra/controllers/status-sync-crm-to-aircall.controller';
import type { BaseUsecase } from '@usecases/base-usecase';

export type StatusSyncCrmToAircallRequest = StatusSyncCrmToAircallInput;

export type StatusSyncCrmToAircallUsecase = BaseUsecase<
  StatusSyncCrmToAircallRequest,
  void
>;

export interface StatusSyncCrmToAircallMapper {
  mapPhoneStatus(status: string): Promise<string>;
  mapAgentStatus(status: string): Promise<AgentStatus>;
  getDefaultCRMKey(status: StatusSyncCrmToAircallInput): Promise<string>;
}

export interface StatusSyncCrmToAircallUserRepository {
  setCurrentCRMStatus(status: string): Promise<void>;
  getPhoneStatus(): Promise<AgentStatus | undefined>;
  setPhoneStatus(status: AgentStatus): Promise<void>;
}

export interface StatusSyncCrmToAircallIntegrationRepository {
  getLatestIntegrationSettings(): Promise<IntegrationSettings | undefined>;
}

export interface StatusSyncCrmToAircallEventPublisher {
  publishStatus(status: string): Promise<void>;
}

export class StatusSyncCrmToAircall implements StatusSyncCrmToAircallUsecase {
  constructor(
    private userRepository: StatusSyncCrmToAircallUserRepository,
    private integrationRepository: StatusSyncCrmToAircallIntegrationRepository,
    private mapper: StatusSyncCrmToAircallMapper,
    private publisher: StatusSyncCrmToAircallEventPublisher,
  ) {}

  async run(status: StatusSyncCrmToAircallRequest): Promise<void> {
    await this.userRepository.setCurrentCRMStatus(status.statusId);

    //Get phone status setting from integration settings
    const phoneStatusSetting = await this.getPhoneStatusByIntegrationSetting(
      status,
    );
    if (
      !phoneStatusSetting ||
      phoneStatusSetting === IntegrationSettingsValueType.NOTHING
    ) {
      return;
    }

    // Map phone setting to centralised agent status
    const mappedAgentStatus = await this.mapper.mapAgentStatus(
      phoneStatusSetting,
    );
    const currentAgentStatus = await this.userRepository.getPhoneStatus();
    if (currentAgentStatus === mappedAgentStatus) {
      return;
    }

    // Get mapped phone status to be published on bus/hydra
    const updatedPhoneStatus = await this.mapper.mapPhoneStatus(
      phoneStatusSetting,
    );

    Promise.all([
      this.publisher.publishStatus(updatedPhoneStatus),
      this.userRepository.setPhoneStatus(mappedAgentStatus),
    ]);
  }

  private async getPhoneStatusByIntegrationSetting(
    status: StatusSyncCrmToAircallRequest,
  ): Promise<string> {
    const { statusApiName, isOffline } = status;
    const setting =
      await this.integrationRepository.getLatestIntegrationSettings();

    //Get configureOmniChannelOn setting to get if it is computed on default or custom presence status
    const crmStatusConfiguration =
      setting?.sections.general?.settings[
        IntegrationSettingKeys.CONFIGURE_OMNICHANNEL_ON
      ];

    let settingKey = statusApiName; // set key to custom status by default
    if (
      !crmStatusConfiguration ||
      crmStatusConfiguration === ConfigureOmniChannelOn.PARENT_STATUS ||
      isOffline
    ) {
      settingKey = await this.mapper.getDefaultCRMKey(status);
    }

    const phoneStatusSetting = setting?.sections[
      IntegrationSettingKeys.OMNICHANNEL_SF_TO_AIRCALL
    ]?.settings[settingKey] as string;

    return phoneStatusSetting ?? '';
  }
}
